/**
 * Utility functions for localStorage operations
 */

export const STORAGE_KEYS = {
  CLAIMS_FILTERS_APPLIED: 'claims_filters_applied',
  SELECTED_LOB_ID: 'selectedLobId',
} as const;

/**
 * Safely gets an item from localStorage
 * @param key - The localStorage key
 * @returns The parsed value or null if not found/invalid
 */
export const getFromLocalStorage = <T>(key: string): T | null => {
  if (typeof window === 'undefined') {
    return null; // SSR safety
  }

  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    // eslint-disable-next-line
    console.warn(`Error reading from localStorage with key "${key}":`, error);
    return null;
  }
};

/**
 * Safely sets an item in localStorage
 * @param key - The localStorage key
 * @param value - The value to store
 */
export const setToLocalStorage = <T>(key: string, value: T): void => {
  if (typeof window === 'undefined') {
    return; // SSR safety
  }

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // eslint-disable-next-line
    console.warn(`Error writing to localStorage with key "${key}":`, error);
  }
};

/**
 * Safely removes an item from localStorage
 * @param key - The localStorage key
 */
export const removeFromLocalStorage = (key: string): void => {
  if (typeof window === 'undefined') {
    return; // SSR safety
  }

  try {
    localStorage.removeItem(key);
  } catch (error) {
    // eslint-disable-next-line
    console.warn(`Error removing from localStorage with key "${key}":`, error);
  }
};

/**
 * Checks if localStorage is available
 * @returns true if localStorage is available, false otherwise
 */
export const isLocalStorageAvailable = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get the stored lobId from localStorage
 * @returns the stored lobId as a number, or null if not found
 */
export const getStoredLobId = (): number | null => {
  const storedValue = getFromLocalStorage<string>(STORAGE_KEYS.SELECTED_LOB_ID);
  return storedValue ? parseInt(storedValue, 10) : null;
};

/**
 * Store the lobId in localStorage
 * @param lobId - The lobId to store
 */
export const setStoredLobId = (lobId: number): void => {
  setToLocalStorage(STORAGE_KEYS.SELECTED_LOB_ID, String(lobId));
};

/**
 * Remove the stored lobId from localStorage
 */
export const removeStoredLobId = (): void => {
  removeFromLocalStorage(STORAGE_KEYS.SELECTED_LOB_ID);
};
